import type { Ref } from 'vue';
import { computed, nextTick, ref } from 'vue';
import { IndicationHelper, type ContentTagCategory } from '@getaccept/dsr-shared';
import { ContentTagService } from '../../../api/dealroom-tags/services/content-tag.service';
import type {
  CreateTagPayload,
  DeleteTagPayload,
  MoveTagPayload,
  UpdateTagPayload,
} from '../types/tag-payload';

export const useContentTags = () => {
  const categories: Ref<ContentTagCategory[]> = ref([]);
  const totalTagCount = computed(() => categories.value.flatMap(({ tags }) => tags || []).length);
  const loading = ref(false);

  const load = async () => {
    loading.value = true;
    try {
      categories.value = await ContentTagService.load();
    } catch (e) {
      console.error(e);
    } finally {
      loading.value = false;
    }
  };

  const createCategory = async (category: ContentTagCategory) => {
    categories.value = [category, ...categories.value];
    await nextTick();
    IndicationHelper.addBlinkingClass(category.id);
  };

  const deleteCategory = (id: string) => {
    categories.value = categories.value.filter(category => category.id !== id);
  };

  const updateCategory = (updatedCategory: ContentTagCategory) => {
    categories.value = categories.value.map(category =>
      category.id === updatedCategory.id ? updatedCategory : category
    );
    IndicationHelper.addBlinkingClass(updatedCategory.id);
  };

  const createTag = async ({ tag, categoryId }: CreateTagPayload) => {
    categories.value = categories.value.map(category =>
      category.id === categoryId
        ? {
            ...category,
            tags: category.tags ? [...category.tags, tag] : [tag],
          }
        : category
    );
    await nextTick();
    IndicationHelper.addBlinkingClass(tag.id);
  };

  const deleteTag = ({ tagId, categoryId }: DeleteTagPayload) => {
    categories.value = categories.value.map(category =>
      category.id === categoryId
        ? {
            ...category,
            tags: category.tags.filter(tag => tag.id !== tagId),
          }
        : category
    );
  };

  const updateTag = ({ updatedTag, categoryId }: UpdateTagPayload) => {
    categories.value = categories.value.map(category =>
      category.id === categoryId
        ? {
            ...category,
            tags: category.tags.map(tag => (tag.id === updatedTag.id ? updatedTag : tag)),
          }
        : category
    );
    IndicationHelper.addBlinkingClass(updatedTag.id);
  };

  const moveTag = async ({ tagToMove, sourceCategoryId, targetCategoryId }: MoveTagPayload) => {
    const categoriesBeforeMove = [...categories.value];
    try {
      categories.value = categories.value.map(category => {
        if (category.id === sourceCategoryId) {
          return { ...category, tags: category.tags.filter(tag => tag.id !== tagToMove.id) };
        }
        if (category.id === targetCategoryId) {
          return { ...category, tags: category.tags ? [...category.tags, tagToMove] : [tagToMove] };
        }
        return category;
      });
      await ContentTagService.updateTag({ targetCategoryId }, tagToMove.id, sourceCategoryId);
      IndicationHelper.addBlinkingClass(tagToMove.id);
    } catch {
      categories.value = categoriesBeforeMove;
    }
  };

  return {
    categories,
    loading,
    load,
    createCategory,
    createTag,
    deleteCategory,
    deleteTag,
    updateCategory,
    updateTag,
    moveTag,
    totalTagCount,
  };
};
